// @ts-nocheck

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SearchBar } from "@/components/search/SearchBar";
import { PostCard } from "@/components/post/PostCard";
import { cn } from "@/lib/utils/cn";
import { formatCount, formatTimeAgo } from "@/lib/utils/format";
import { Avatar } from "@/components/ui/Avatar";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { IconLoader, IconSearch } from "@/components/ui/Icons";
import { Link } from "react-router-dom";
import type { Post, PublicProfile, Topic } from "@/types";

interface SearchPageClientProps {
  initialQuery: string;
  initialType: "post" | "user" | "topic";
  initialSort: "relevance" | "new" | "top";
}

type SearchTab = "post" | "user" | "topic";

export function SearchPageClient({ initialQuery, initialType, initialSort }: SearchPageClientProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState<SearchTab>(initialType);
  const [sort, setSort] = useState(initialSort);
  const [results, setResults] = useState<{ posts: Post[]; users: PublicProfile[]; topics: Topic[] }>({
    posts: [], users: [], topics: [],
  });
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [took, setTook] = useState(0);
  const [total, setTotal] = useState(0);

  const doSearch = useCallback(async (q: string, type: SearchTab, s: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setHasSearched(true);

    const params = new URLSearchParams({ q, type, sort: s, limit: "20" });
    navigate(`/search?${params.toString()}`);

    try {
      const res = await fetch(`/api/search?${params}`);
      const data = await res.json();
      setResults({ posts: data.posts || [], users: data.users || [], topics: data.topics || [] });
      setTook(data.took_ms || 0);
      setTotal(data.total || 0);
    } catch {
      setResults({ posts: [], users: [], topics: [] });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (initialQuery) doSearch(initialQuery, initialType, initialSort);
  }, []);

  const handleSearch = (q: string) => {
    setQuery(q);
    doSearch(q, activeTab, sort);
  };

  const handleTabChange = (tab: SearchTab) => {
    setActiveTab(tab);
    if (query) doSearch(query, tab, sort);
  };

  const tabs: { value: SearchTab; label: string; count: number }[] = [
    { value: "post", label: "Posts", count: results.posts.length },
    { value: "user", label: "Users", count: results.users.length },
    { value: "topic", label: "Topics", count: results.topics.length },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Search input */}
      <div className="mb-6">
        <SearchBar
          autoFocus={!initialQuery}
          placeholder="Search posts, topics, users..."
          onSearch={handleSearch}
        />
      </div>

      {query && hasSearched && (
        <>
          {/* Stats */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-text-muted">
              {loading ? "Searching..." : `About ${formatCount(total)} results (${took}ms)`}
            </p>
            {/* Sort */}
            <div className="flex gap-1">
              {(["relevance", "new", "top"] as const).map(s => (
                <button key={s} onClick={() => { setSort(s); doSearch(query, activeTab, s); }}
                  className={cn("px-3 py-1 rounded-lg text-xs font-medium capitalize transition-colors",
                    sort === s ? "bg-brand text-brand-foreground" : "text-text-muted hover:bg-muted"
                  )}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-border mb-5">
            {tabs.map(tab => (
              <button key={tab.value} onClick={() => handleTabChange(tab.value)}
                className={cn("flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
                  activeTab === tab.value
                    ? "border-brand text-brand"
                    : "border-transparent text-text-muted hover:text-foreground"
                )}>
                {tab.label}
                {tab.count > 0 && (
                  <span className={cn("text-2xs px-1.5 py-0.5 rounded-full",
                    activeTab === tab.value ? "bg-brand/15 text-brand" : "bg-muted text-text-subtle")}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <IconLoader className="w-8 h-8 text-brand" />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}>
                {activeTab === "post" && (
                  results.posts.length > 0 ? (
                    <div className="space-y-3">
                      {results.posts.map(post => (
                        <PostCard key={post.id} post={post} userId={null} />
                      ))}
                    </div>
                  ) : <EmptyResults query={query} type="posts" />
                )}

                {activeTab === "user" && (
                  results.users.length > 0 ? (
                    <div className="space-y-2">
                      {results.users.map(user => (
                        <UserResult key={user.id} user={user} />
                      ))}
                    </div>
                  ) : <EmptyResults query={query} type="users" />
                )}

                {activeTab === "topic" && (
                  results.topics.length > 0 ? (
                    <div className="space-y-2">
                      {results.topics.map(topic => (
                        <TopicResult key={topic.id} topic={topic} />
                      ))}
                    </div>
                  ) : <EmptyResults query={query} type="topics" />
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </>
      )}

      {/* Empty state */}
      {!query && (
        <div className="flex flex-col items-center py-20 text-center">
          <div className="w-16 h-16 rounded-3xl bg-muted flex items-center justify-center mb-4">
            <IconSearch className="w-8 h-8 text-text-subtle" />
          </div>
          <p className="text-lg font-semibold text-foreground">Search Squids</p>
          <p className="text-sm text-text-muted mt-2 max-w-xs">
            Find posts, communities, and users across all of Squids
          </p>
        </div>
      )}
    </div>
  );
}

function UserResult({ user }: { user: PublicProfile }) {
  return (
    <Link to={`/profile/${user.username}`}
      className="card p-4 flex items-center gap-3 hover:shadow-card-hover transition-shadow">
      <Avatar src={user.avatar_url} alt={user.display_name} size="lg" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-foreground">{user.display_name}</span>
          {user.is_verified && <VerifiedBadge size="sm" />}
        </div>
        <p className="text-sm text-text-muted">@{user.username}</p>
        {user.bio && <p className="text-xs text-text-subtle mt-1 line-clamp-1">{user.bio}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-foreground">{formatCount(user.karma_score)}</p>
        <p className="text-xs text-text-subtle">karma</p>
      </div>
    </Link>
  );
}

function TopicResult({ topic }: { topic: Topic }) {
  return (
    <Link to={`/topics/${topic.slug}`}
      className="card p-4 flex items-center gap-3 hover:shadow-card-hover transition-shadow">
      <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
        style={{ backgroundColor: `${topic.color}20` }}>
        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: topic.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-foreground">{topic.name}</span>
          {topic.is_verified && <VerifiedBadge size="sm" />}
        </div>
        {topic.description && (
          <p className="text-xs text-text-muted mt-0.5 line-clamp-1">{topic.description}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-foreground">{formatCount(topic.subscriber_count)}</p>
        <p className="text-xs text-text-subtle">members</p>
      </div>
    </Link>
  );
}

function EmptyResults({ query, type }: { query: string; type: string }) {
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
        <IconSearch className="w-6 h-6 text-text-subtle" />
      </div>
      <p className="text-sm font-medium text-foreground">No {type} found for &ldquo;{query}&rdquo;</p>
      <p className="text-xs text-text-muted mt-1">Try different keywords or browse topics instead</p>
    </div>
  );
}
